import * as React from 'react';

import { ButtonSpinner, Checkbox, DropdownInput } from '../../../Shared/index';
import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility'
import { requestPost } from '../../../../modules/request/index';
import DemoGuardedButton from '../shared/demoGuardedButton';

interface MoveStudentsModalProps {
  classroom: any;
  classrooms: Array<any>;
  close: () => void;
  onSuccess: (message: string) => void;
  selectedStudentIds: any;
}

const MoveStudentsModal: React.FC<MoveStudentsModalProps> = ({ classroom, classrooms, close, onSuccess, selectedStudentIds }) => {
  const { modalRef, } = useModalAccessibility(close);

  const [checkboxOne, setCheckboxOne] = React.useState<boolean>(false);
  const [newClassroomId, setNewClassroomId] = React.useState<string | number | undefined>(undefined);
  const [waiting, setWaiting] = React.useState<boolean>(false);

  const studentOrStudents = () => {
    return selectedStudentIds.length === 1 ? 'Student' : 'Students';
  };

  const classroomOptions = () => {
    return classrooms.map((classroom: any) => ({
      label: classroom.name,
      value: classroom.id,
    }));
  };

  const handleClassroomChange = (option: any) => {
    setNewClassroomId(option.value);
  };

  const moveStudents = () => {
    setWaiting(true);
    requestPost(
      `/teachers/classrooms/${classroom.id}/students/move_students`,
      { new_classroom_id: newClassroomId, student_ids: selectedStudentIds },
      (body) => {
        const newClassroom = classrooms.find((cls: any) => cls.id === newClassroomId);
        const successMessage = `${studentOrStudents()} moved to ${newClassroom.name}`;
        setWaiting(false);
        onSuccess(successMessage);
        close();
      }
    );
  };

  const submitButtonClass = () => {
    let buttonClass = 'quill-button contained green medium focus-on-light';
    if (!(newClassroomId && checkboxOne)) {
      buttonClass += ' disabled';
    }
    return buttonClass;
  };

  const toggleCheckbox = () => {
    setCheckboxOne((prev) => !prev);
  };

  const renderSubmitButton = () => {
    if (waiting) {
      return (
        <button className={submitButtonClass()}>
          <ButtonSpinner />
        </button>
      );
    }
    return (
      <DemoGuardedButton className={submitButtonClass()} onClick={moveStudents}>
        Move {studentOrStudents().toLowerCase()}
      </DemoGuardedButton>
    );
  };

  const options = classroomOptions();
  const classroomOptionsForDropdown = options.filter((opt) => opt.value !== classroom.id);
  const numberOfSelectedStudents = selectedStudentIds.length;

  return (
    <div className="modal-container move-students-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="moveStudentsModalTitle"
        aria-modal="true"
        className="move-students-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div>
          <h3 className="title" id="moveStudentsModalTitle">
            Move {numberOfSelectedStudents} {studentOrStudents().toLowerCase()} to a new class
          </h3>
        </div>
        <p>
          All of the data from the activities that your{' '}
          {numberOfSelectedStudents === 1 ? 'student has' : 'students have'} started or completed will be moved.
        </p>
        <DropdownInput
          className="large bordered class focus-on-light"
          handleChange={handleClassroomChange}
          label="Class"
          options={classroomOptionsForDropdown}
          value={options.find((co) => co.value === newClassroomId)}
        />
        <div className="checkboxes">
          <Checkbox
            label="I understand that any activities that have not been started will not be transferred."
            onClick={toggleCheckbox}
            selected={checkboxOne}
          />
        </div>
        <div className="form-buttons">
          <button className="quill-button outlined green medium focus-on-light" onClick={close}>
            Cancel
          </button>
          {renderSubmitButton()}
        </div>
      </div>
    </div>
  );
};

export default MoveStudentsModal;
